import { Container, Col, Row } from 'react-bootstrap';
import { useIsAuthenticated } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import useHtmlTitle from '../Utilities/useHtmlTitle';
import useBodyClass from '../Utilities/useBodyClass';
import StandardPathway from '../tiles/StandardPathway';
import DigitalIdentityButton from '../Buttons/DigitalIdentityButton';
import BodyText from '../BodyText';
import InTextLink from '../InTextLink';
import HeaderIntroText from '../HeaderIntroText';
import { clearGetStartedNotification, getGetStartedNotification } from '../../storage/notification';
import NotificationMessage from '../Alert/NotificationMessage';

const GetStarted = () => {
    const isAuthenticated = useIsAuthenticated();
    useHtmlTitle('NMI Services portal | NMI');
    useBodyClass('home');
    const getStartedNotification = getGetStartedNotification();
    const getStartedMessage = getStartedNotification
        ? <NotificationMessage canClose onClose={clearGetStartedNotification} {...getStartedNotification} />
        : null;

    if (isAuthenticated) {
        return <Navigate to='/dashboard' />;
    }

    return (
        <>
            <Container fluid className='default-banner-background gs-wrapper mb-5'>
                <Container>
                    <Row>
                        <Col md={12} lg={9}>
                            <h1 id='page-title-nofocus' tabIndex={-1} className='banner-title mb-0'>
                                {'National Measurement Institute (NMI) '}
                                <span className='text-nowrap'>Services portal</span>
                            </h1>
                        </Col>
                    </Row>
                </Container>
            </Container>
            {getStartedMessage && (
                <Container className='gs-wrapper'>
                    <Row>
                        <Col>
                            {getStartedMessage}
                        </Col>
                    </Row>
                </Container>
            )}
            <Container>
                <HeaderIntroText className='col-12 col-lg-9'>
                    To access the NMI Services portal you will need to log in using your Digital ID.
                </HeaderIntroText>
                <Row className='mb-3'>
                    <h2 className='visually-hidden'>Please choose from the following options:</h2>
                    <Col md={6}>
                        {/* <StandardPathway
                            type='external'
                            title='Create a Digital ID'
                            linkDescription='Find out how to set up your Digital ID'
                            linkHref='https://www.digitalidsystem.gov.au/set-up-your-digital-id'
                            target='_blank'
                        /> */}
                        <StandardPathway
                            type='internal'
                            title='How to set up access'
                            bodyText='Step by step guide on setting up access to the NMI Services portal'
                            linkDescription='Learn more'
                            to='/help-guide/how-to-setup-access'
                        />
                    </Col>
                    <Col md={6}>
                        <StandardPathway
                            type='internal'
                            title='Log in'
                            linkDescription='Continue with Digital ID'
                            to='/dashboard'
                            digitalIdentity
                        />
                    </Col>
                </Row>
                <Row className='mb-5'>
                    <Col md={6} />
                    <Col md={6}>
                        <p>
                            <InTextLink
                                target='_blank'
                                href='https://www.digitalidsystem.gov.au'
                            >
                                Digital ID
                            </InTextLink>
                            is a safe, secure and convenient way to prove and reuse your identity online.
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <h2 id='what-to-do' className='h3'>
                            What do I need to do?
                        </h2>
                        <BodyText className='col-12 col-lg-9'>
                            { /* eslint-disable-next-line max-len */}
                            Before you can access the NMI Services portal you need to have a personal Digital ID and make sure that it&apos;s linked to your place of work. If your Digital ID is not linked to your place of work, the principal authority for the business can manage this via the ATO&apos;s&nbsp;
                            <InTextLink
                                target='_blank'
                                href='https://info.authorisationmanager.gov.au/'
                            >
                                Relationship Authorisation Manager
                            </InTextLink>
                            {' '}
                            (RAM) system.
                        </BodyText>
                        <BodyText className='col-12 col-lg-9'>
                            { /* eslint-disable-next-line max-len */}
                            Once you have a Digital ID and log in to the NMI Services portal, you can then provide third party access to consultants that may be assisting your business on your NMI journey.
                        </BodyText>
                        <DigitalIdentityButton
                            to='/dashboard'
                            className='mb-4'
                        >
                            <span className='text-nowrap'>Continue with Digital ID</span>
                        </DigitalIdentityButton>
                        <BodyText className='col-12 col-lg-9'>
                            <InTextLink
                                target='_blank'
                                href='https://www.digitalidsystem.gov.au/'
                            >
                                Digital ID
                            </InTextLink>
                            is a safe, secure and convenient way to prove and reuse your identity online.
                        </BodyText>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default GetStarted;
